import { NgFor, NgTemplateOutlet, NgClass, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { AgrDatePipe, AgrNumberPipe, LocaleService } from '@next/core-lib/i18n';
import { DataSeriesParameter } from '@next/core-lib/models';
import { Observable, Subscription } from 'rxjs';
import { BenchmarkSettings } from 'src/app/farmer-insights/models/farmer-insights.model';

import { TableColumn, TableConfig } from './../../../../../shared/components/table/table.model';

const MAX_NUM_OF_COLUMNS = 21;
const MAX_NUM_OF_ROWS_PER_PAGE = 15;

@Component({
  selector: 'app-data-view-table-detailed-print',
  templateUrl: './data-view-table-detailed-print.component.html',
  styleUrls: ['./data-view-table-detailed-print.component.scss'],
  imports: [NgFor, NgTemplateOutlet, NgClass, NgIf],
})
export class DataViewTableDetailedPrintComponent implements OnInit, OnDestroy {
  private decimalPipe = inject(AgrNumberPipe);
  private datePipe = inject(AgrDatePipe);
  private readonly locale = inject(LocaleService);

  @Input() config: Observable<TableConfig>;
  @Input() parameters: DataSeriesParameter[];
  @Input() benchmarkSettings: BenchmarkSettings;

  tableColumns: TableColumn[] = [];
  numberOfPages: number;
  tablePagesData: Record<number, unknown[]> = {};
  subscription: Subscription;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getNumberOfBenchmarksToShow(): number[] {
    const benchmarksToShowIndices = [];
    let count = 1;
    if (this.benchmarkSettings.lastWeeks) {
      benchmarksToShowIndices.push(count++);
    }
    if (this.benchmarkSettings.selectedPeriod) {
      benchmarksToShowIndices.push(count++);
    }
    if (this.benchmarkSettings.customerAverageSelectedPeriod) {
      benchmarksToShowIndices.push(count);
    }
    return benchmarksToShowIndices;
  }

  ngOnInit(): void {
    this.subscription = this.config.subscribe((c) => {
      this.tablePagesData = {};
      const data = [...c.dataSource.data];

      this.tableColumns = [...c.columns].slice(0, MAX_NUM_OF_COLUMNS);
      this.numberOfPages = Math.ceil(data.length / MAX_NUM_OF_ROWS_PER_PAGE);

      for (let page = 0; page < this.numberOfPages; page++) {
        const indexStart = page * MAX_NUM_OF_ROWS_PER_PAGE;

        this.tablePagesData[page] = [...data].slice(
          indexStart,
          indexStart + MAX_NUM_OF_ROWS_PER_PAGE,
        );
      }
    });
  }

  arrayOne(n: number): unknown[] {
    return Array(n);
  }

  /* eslint-disable */
  formatValue(row: Record<string, any>, column: TableColumn): string {
    const kpi: DataSeriesParameter = this.parameters.find((c) => c.code === column.id);
    if (
      row[column.id] === undefined ||
      (column.type !== 'string' && (isNaN(row[column.id]) || !isFinite(row[column.id])))
    ) {
      return this.locale.translate('n/a');
    }
    /* eslint-enable */
    switch (column.type) {
      case 'number':
        return this.decimalPipe.transform(
          row[column.id],
          kpi !== undefined ? `1.${kpi.numberOfDecimals}-${kpi.numberOfDecimals}` : undefined,
        );
      case 'date':
        return this.datePipe.transform(row[column.id]);
      default:
        return row[column.id] as string;
    }
  }

  getTotalValue(colId: string, index: number): string {
    if (this.tableColumns) {
      const col = this.tableColumns.find((c) => c.id === colId);
      if (col !== undefined) {
        return col.customfooterValues[index] as string;
      }
    }
    return '';
  }
}
